/* Footer / sign-up page newsletter form.

   A signup writes to TWO places, for two different reasons.

   1. Klaviyo, via /client/subscriptions. That is where the campaigns and flows
      live. The endpoint exists for exactly this: CORS-enabled, takes the PUBLIC
      company id rather than a private key, meant to be called from a browser.
      Nothing secret is shipped to the page -- the company id is already in the
      Klaviyo onsite script tag on every page of the site.

   2. Shopify, via its own customer endpoint, which sets email marketing consent.
      This is not duplication for its own sake. The free care kit discount is
      restricted to the Shopify customer segment "Email subscribers"
      (email_subscription_status = 'SUBSCRIBED'). A profile that exists only in
      Klaviyo is not in that segment, so the code would be shown on the sign-up
      page and then rejected at checkout. Registering consent in Shopify too is
      what makes the code the visitor is handed actually work.

   Klaviyo is what decides success: it owns the list, and its response is the one
   that tells us the address was accepted. The Shopify post is fire-and-forget --
   it usually fails only because the customer already exists and is already
   subscribed, which is not a reason to tell someone their signup did not work.

   Every Klaviyo response other than 2xx is reported to the visitor rather than
   swallowed, because a signup that fails silently is worse than one that fails
   visibly: the visitor walks away believing they are on the list. */
(function () {
    'use strict';

    var ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
    var REVISION = '2024-10-15';

    /* Liquid's {% form %} tag only lets the section give the consent form a class
       and an id, not a data attribute, so it is matched on the class. The data
       attribute is kept in the selector as well in case the markup gains one. */
    var CONSENT = 'form.dico-nl-consent, form[data-dico-consent]';

    /* Deliberately loose. Address validation is not really a regex problem, and a
       strict pattern rejects valid addresses -- Klaviyo is the authority. This
       only catches the obvious typo before spending a request on it. */
    function looksLikeEmail(value) {
        return /^[^@\s]+@[^@\s.]+\.[^@\s]{2,}$/.test(value);
    }

    function closestSection(el) {
        while (el && el.classList && !el.classList.contains('dico-nl')) {
            el = el.parentElement;
        }
        return el || document;
    }

    function setStatus(form, message, isError) {
        var box = form.querySelector('[data-nl-status]');
        if (!box) { return; }
        box.textContent = message || '';
        if (isError) {
            box.classList.add('is-error');
        } else {
            box.classList.remove('is-error');
        }
        if (message) {
            box.classList.add('is-visible');
        } else {
            box.classList.remove('is-visible');
        }
    }

    /* Marketing consent in Shopify, so the new subscriber lands in the
       "Email subscribers" segment the care kit code is gated to.

       Uses the real {% form 'customer' %} rendered by the section, so Shopify's
       own form_type and utf8 fields come along -- posting a hand-built body to
       /contact is rejected. */
    function registerShopifyConsent(scope, email) {
        var consent = scope.querySelector(CONSENT);
        if (!consent) { return; }

        var field = consent.querySelector('input[name="contact[email]"]');
        if (!field) { return; }
        field.value = email;

        try {
            window.fetch(consent.getAttribute('action') || '/contact', {
                method: 'POST',
                body: new FormData(consent),
                credentials: 'same-origin'
            })['catch'](function () {});
        } catch (e) {}
    }

    function selectCode(scope) {
        var el = scope.querySelector('[data-nl-code-value]');
        if (!el || !window.getSelection || !document.createRange) { return; }
        var range = document.createRange();
        range.selectNodeContents(el);
        var sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
    }

    function bindCopy(scope) {
        var button = scope.querySelector('[data-nl-copy]');
        if (!button || button.dicoCopyBound) { return; }
        button.dicoCopyBound = true;

        button.addEventListener('click', function () {
            var code = button.getAttribute('data-code') || '';
            var label = button.getAttribute('data-label') || button.textContent;

            var done = function () {
                button.textContent = button.getAttribute('data-copied') || 'Copied';
                window.setTimeout(function () { button.textContent = label; }, 2000);
            };

            /* clipboard.writeText needs a secure context and can be blocked, so
               there is a selection fallback -- the code is useless to the visitor
               if they cannot get it out of the page. */
            if (window.navigator.clipboard && window.navigator.clipboard.writeText) {
                window.navigator.clipboard.writeText(code).then(done)['catch'](function () {
                    selectCode(scope);
                });
                return;
            }
            selectCode(scope);
        });
    }

    function subscribe(form) {
        var scope = closestSection(form);
        var input = form.querySelector('input[type="email"]');
        var button = form.querySelector('button[type="submit"]');
        var companyId = form.getAttribute('data-company');
        var listId = form.getAttribute('data-list');
        var email = (input && input.value ? input.value : '').trim();

        if (!looksLikeEmail(email)) {
            setStatus(form, form.getAttribute('data-msg-invalid'), true);
            if (input) { input.focus(); }
            return;
        }

        if (!companyId || !listId) {
            setStatus(form, form.getAttribute('data-msg-error'), true);
            return;
        }

        form.classList.add('is-busy');
        if (button) { button.disabled = true; }
        setStatus(form, form.getAttribute('data-msg-busy'), false);

        /* Sent alongside the Klaviyo call rather than after it. The two are
           independent, and the visitor should not wait on the slower one. */
        if (form.getAttribute('data-shopify-consent') === 'true') {
            registerShopifyConsent(scope, email);
        }

        var payload = {
            data: {
                type: 'subscription',
                attributes: {
                    custom_source: form.getAttribute('data-source') || 'Website footer',
                    profile: {
                        data: {
                            type: 'profile',
                            attributes: { email: email }
                        }
                    }
                },
                relationships: {
                    list: { data: { type: 'list', id: listId } }
                }
            }
        };

        window.fetch(ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                revision: REVISION
            },
            body: JSON.stringify(payload)
        }).then(function (res) {
            if (res.status >= 200 && res.status < 300) {
                /* Ties the new profile to this browsing session, so the signup can
                   be attributed and the visitor's later activity lands on the same
                   profile. Guarded: the onsite script may be blocked. */
                try {
                    if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
                        window.klaviyo.identify({ email: email });
                    }
                } catch (e) {}

                form.classList.remove('is-busy');
                form.classList.add('is-done');
                setStatus(form, form.getAttribute('data-msg-success'), false);
                input.value = '';
                bindCopy(scope);
                return;
            }
            throw new Error('HTTP ' + res.status);
        })['catch'](function () {
            form.classList.remove('is-busy');
            if (button) { button.disabled = false; }
            setStatus(form, form.getAttribute('data-msg-error'), true);
        });
    }

    function bind(form) {
        if (form.dicoNlBound) { return; }
        form.dicoNlBound = true;
        form.addEventListener('submit', function (event) {
            event.preventDefault();
            subscribe(form);
        });
    }

    function boot() {
        var forms = document.querySelectorAll('form[data-dico-newsletter]');
        for (var i = 0; i < forms.length; i++) { bind(forms[i]); }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }

    /* Re-bind after a theme-editor re-render. */
    document.addEventListener('shopify:section:load', boot);
}());
